import React from 'react';
import Icon from '../../../components/AppIcon';

const ReferencesSection = () => {
  const professionalReferences = [
    {
      name: "Sarah Al-Mahmoud",
      title: "Senior Partner",
      firm: "Al-Mahmoud & Associates",
      location: "Dubai, UAE",
      specialization: "Commercial Litigation",
      testimonial: `Northbridge Valuation & Advisory provided exceptional expert witness services in our complex partnership dissolution case. Their thorough analysis and clear testimony were instrumental in achieving a favorable outcome for our client.`,
      cases: "8 joint cases",
      relationship: "3+ years",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Michael Richardson",
      title: "Managing Partner",
      firm: "Richardson Legal Consultancy",
      location: "Bahrain",
      specialization: "Corporate Disputes",
      testimonial: `Their independence and professional integrity made them an ideal expert witness. The court clearly respected their credentials and accepted their valuation methodology without question.`,
      cases: "12 joint cases",
      relationship: "5+ years",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "Fatima Al-Zahra",
      title: "Senior Counsel",
      firm: "Gulf Legal Partners",
      location: "Doha, Qatar",
      specialization: "IP & Technology Law",
      testimonial: `Exceptional preparation and courtroom presence. They explained complex valuation concepts clearly to the judge and maintained composure during aggressive cross-examination.`,
      cases: "6 joint cases",
      relationship: "2+ years",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face"
    },
    {
      name: "James Thompson",
      title: "Partner",
      firm: "Thompson & Associates",
      location: "Abu Dhabi, UAE",
      specialization: "Banking & Finance",
      testimonial: `Reliable, thorough, and professional. Their expert reports are comprehensive and well-reasoned, making our job as advocates much easier. Highly recommended for complex valuation matters.`,
      cases: "15 joint cases",
      relationship: "4+ years",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
    }
  ];

  const attorneyConcerns = [
    {
      concern: "Expert Witness Reliability",
      description: "Will the expert maintain consistent positions and provide dependable testimony?",
      ourApproach: [
        "Comprehensive case preparation with detailed documentation",
        "Consistent methodology application across similar cases",
        "Regular communication and case status updates",
        "Professional liability insurance and quality assurance"
      ],
      icon: "Shield"
    },
    {
      concern: "Communication Effectiveness",
      description: "Can the expert explain complex concepts clearly to judges and juries?",
      ourApproach: [
        "Plain language explanations with practical analogies",
        "Visual aids and demonstrative materials preparation",
        "Mock testimony practice and presentation coaching",
        "Cultural sensitivity and regional communication styles"
      ],
      icon: "MessageCircle"
    },
    {
      concern: "Courtroom Presence",
      description: "Will the expert maintain professionalism under aggressive cross-examination?",
      ourApproach: [
        "Extensive cross-examination preparation and practice",
        "Stress testing of opinions and methodology",
        "Professional demeanor training and coaching",
        "Experience with hostile questioning techniques"
      ],
      icon: "User"
    },
    {
      concern: "Cost Predictability",
      description: "How can legal teams budget effectively for expert witness services?",
      ourApproach: [
        "Transparent fee structures with detailed estimates",
        "Fixed-fee options for standard engagements",
        "Regular billing updates and cost monitoring",
        "Efficient project management to control costs"
      ],
      icon: "DollarSign"
    }
  ];

  const credibilityFactors = [
    {
      factor: "Professional Credentials",
      details: [
        "CVA (Certified Valuation Analyst) certification",
        "15+ years valuation experience",
        "Court-qualified expert in multiple jurisdictions",
        "Professional association memberships"
      ]
    },
    {
      factor: "Testimony Experience",
      details: [
        "50+ court testimonies across GCC region",
        "100+ hours of deposition experience",
        "95% opinion acceptance rate",
        "Zero ethical violations or sanctions"
      ]
    },
    {
      factor: "Independence Framework",
      details: [
        "Comprehensive conflict checking procedures",
        "Independence affidavits and declarations",
        "Professional liability insurance coverage",
        "Ethical standards compliance monitoring"
      ]
    },
    {
      factor: "Regional Expertise",
      details: [
        "Deep understanding of US & GCC business practices",
        "Familiarity with local court procedures",
        "Cultural sensitivity and language capabilities",
        "Established relationships with legal community"
      ]
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-green-100 text-green-700 rounded-full px-4 py-2 text-sm font-medium mb-6">
            <Icon name="Users" size={16} className="mr-2" />
            Professional References
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Trusted by Leading Legal Professionals
          </h2>
          
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our reputation in the legal community is built on consistent delivery of credible expert testimony and professional service excellence.
          </p>
        </div>

        {/* Professional References */}
        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {professionalReferences?.map((reference, index) => (
            <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-border hover:shadow-md transition-shadow duration-300">
              <div className="flex items-start space-x-4 mb-6">
                <img
                  src={reference?.avatar}
                  alt={reference?.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-primary mb-1">
                    {reference?.name}
                  </h3>
                  <p className="text-sm text-text-secondary mb-1">
                    {reference?.title}, {reference?.firm}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-text-muted">
                    <span className="flex items-center space-x-1">
                      <Icon name="MapPin" size={12} />
                      <span>{reference?.location}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Briefcase" size={12} />
                      <span>{reference?.specialization}</span>
                    </span>
                  </div>
                </div>
              </div>

              <blockquote className="text-text-secondary italic mb-6 leading-relaxed">
                "{reference?.testimonial}"
              </blockquote>

              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <div className="flex items-center space-x-4 text-sm">
                  <span className="flex items-center space-x-1 text-green-600">
                    <Icon name="FileText" size={14} />
                    <span>{reference?.cases}</span>
                  </span>
                  <span className="flex items-center space-x-1 text-blue-600">
                    <Icon name="Clock" size={14} />
                    <span>{reference?.relationship}</span>
                  </span>
                </div>
                <div className="flex space-x-1">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={14} className="text-amber-400 fill-current" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Attorney Concerns & Solutions */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-4">
              Addressing Common Attorney Concerns
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              We understand the challenges legal professionals face when selecting expert witnesses and have developed systematic approaches to address these concerns.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {attorneyConcerns?.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-border">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <Icon name={item?.icon} size={24} className="text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-primary mb-2">
                      {item?.concern}
                    </h4>
                    <p className="text-text-secondary">
                      {item?.description}
                    </p>
                  </div>
                </div>

                <div>
                  <h5 className="text-sm font-semibold text-primary mb-4 uppercase tracking-wide">
                    Our Solution
                  </h5>
                  <div className="space-y-3">
                    {item?.ourApproach?.map((approach, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <Icon name="CheckCircle" size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-text-secondary">{approach}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Credibility Factors */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-primary mb-4">
              Expert Witness Credibility Factors
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Our credibility as expert witnesses is built on a foundation of professional qualifications, extensive experience, and unwavering ethical standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {credibilityFactors?.map((factor, index) => (
              <div key={index} className="space-y-4">
                <h4 className="text-lg font-semibold text-primary flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold text-sm">{index + 1}</span>
                  </div>
                  <span>{factor?.factor}</span>
                </h4>
                <div className="space-y-2 ml-11">
                  {factor?.details?.map((detail, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-text-secondary">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 pt-8 border-t border-border text-center">
            <h4 className="text-xl font-semibold text-primary mb-4">
              Request Professional References
            </h4>
            <p className="text-text-secondary mb-6 max-w-xl mx-auto">
              Contact us to receive detailed professional references and case-specific testimonials from legal professionals in your jurisdiction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2">
                <Icon name="Phone" size={18} />
                <span>Request References</span>
              </button>
              <button className="border border-green-300 hover:bg-green-50 text-green-700 px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2">
                <Icon name="Download" size={18} />
                <span>Download CV</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferencesSection;